// Copyright (c) 2025 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.

import { Tx, TokenInfo } from 'chronik-client';

interface TokenConst {
    tokenId: string;
    token: TokenInfo;
    tx: Tx;
}

/**
 * Hot wallet that handles XECX redemptions to XEC
 * In practice, any redemption must be <= the balance of this wallet
 * to be instantly accepted
 *
 * We alert Cashtab users the "max instant redemption", and include
 * a notice that larger amounts can expect redemption within 24 hrs
 */
export const XECX_SWEEPER_ADDRESS =
    'ecash:qqrjf6n3gvavhw3f35zd0p96m3heujujcs4458vcwf';

export const FIRMA_REDEEM_ADDRESS =
    'ecash:qr8hdk8rxjc5nj6f450eth3nnslxa8k4gysrtyfxc5';

/**
 * Average spot APY from FIRMA_APY_API_URL (AVG of firma_metrics
 * apy_spot). CORS allows any origin. Distinct from /api/apy (XECX).
 */
export const FIRMA_APY_API_URL = 'https://stakedxec.com/api/firma-apy';

/**
 * Firma Agora autobuy price (XEC per FIRMA) from dex.firmaprotocol.com.
 * Cashtab lists FIRMA→XEC redeems at this bid.
 */
export const FIRMA_BID_API_URL = 'https://dex.firmaprotocol.com/api/v1/bid';

/**
 * XECX APY from stakedXec.com (same source as the marketing site hero).
 * Response is `{ apy: number }` as a decimal fraction (e.g. 0.040114 → 4.01%).
 */
export const XECX_APY_API_URL = 'https://stakedxec.com/api/apy';

/** CACHET token - free plays (10 CACHET = 1 free bet) */
export const CACHET_TOKEN_ID =
    'aed861a31b96934b88c0252ede135cb9700d7649f69191235087a3030e553cb1';

/** BLITZ token - Blitzchips.com loyalty token */
export const BLITZ_CHIPS_TOKEN_ID =
    'd1952270af59eb0ae6b07c6ff93c19e1b3ff53fd0595d2ca6f239c55d4b3fd69';

/** Firma CHF (fCHF) — same id as apps/firma FIRMA_CHF_TOKEN_ID. */
export const FIRMA_CHF_TOKEN_ID =
    'a8c83ebe937b9c1b0a7cb7645f43459f57a10043744ddfbc2357e1bd43fe2465';

/** Firma EUR (fEUR) — same id as apps/firma FIRMA_EUR_TOKEN_ID. */
export const FIRMA_EUR_TOKEN_ID =
    'cd751e3dfc23da5344bf66cb8433c31464ab4adbdbc9082f22c71bb53eafa7e8';

// 8-char lokad + 64-char for 32-byte sol pk
export const FIRMA_REDEEM_EMPP_RAW_LENGTH = 72;

export const FIRMA: TokenConst = {
    tokenId: '0387947fd575db4fb19a3e322f635dec37fd192b5941625b66bc4b2c3008cbf0',
    token: {
        tokenId:
            '0387947fd575db4fb19a3e322f635dec37fd192b5941625b66bc4b2c3008cbf0',
        tokenType: {
            protocol: 'ALP',
            type: 'ALP_TOKEN_TYPE_STANDARD',
            number: 0,
        },
        timeFirstSeen: 1740005671,
        genesisInfo: {
            tokenTicker: 'FIRMA',
            tokenName: 'Firma',
            url: 'firma.cash',
            decimals: 4,
            data: '',
            authPubkey:
                '03fba49912622cf8bb5b3729b1b5da3e72c6b57d369c8647f6cc7c6cbed510d105',
        },
        block: {
            height: 884824,
            hash: '0000000000000000042420f0d007398b85c2a4b02d894575de72441c055099fc',
            timestamp: 1740006660,
        },
    },
    tx: {
        txid: '0387947fd575db4fb19a3e322f635dec37fd192b5941625b66bc4b2c3008cbf0',
        version: 2,
        inputs: [
            {
                prevOut: {
                    txid: 'dc13a52129537222797ca324f4f0a130e7ae0f9c57bb36e53ff13c3510b88496',
                    outIdx: 0,
                },
                inputScript:
                    '41a753034ede0327bf8188d14f5407c774c61d78208a317a83bd30475adb14ba0760990fe1f25a4ea885bf0c0ffcae23122148541ac5a89397ebfffb4840f78fbb412103fba49912622cf8bb5b3729b1b5da3e72c6b57d369c8647f6cc7c6cbed510d105',
                sats: 4200n,
                sequenceNo: 4294967295,
                outputScript:
                    '76a914cf76d8e334b149cb49ad1f95de339c3e6e9ed54188ac',
            },
        ],
        outputs: [
            {
                sats: 0n,
                outputScript:
                    '6a504c50534c5032000747454e45534953054649524d41054669726d610a6669726d612e63617368002103fba49912622cf8bb5b3729b1b5da3e72c6b57d369c8647f6cc7c6cbed510d1050401807c814a000003',
            },
            {
                sats: 546n,
                outputScript:
                    '76a914cf76d8e334b149cb49ad1f95de339c3e6e9ed54188ac',
                token: {
                    tokenId:
                        '0387947fd575db4fb19a3e322f635dec37fd192b5941625b66bc4b2c3008cbf0',
                    tokenType: {
                        protocol: 'ALP',
                        type: 'ALP_TOKEN_TYPE_STANDARD',
                        number: 0,
                    },
                    atoms: 1250000000n,
                    isMintBaton: false,
                    entryIdx: 0,
                },
                spentBy: {
                    txid: '83b7d403d5b7ae295ff1e2a66ef220ed6ae190b019df4822643bea545518734a',
                    outIdx: 0,
                },
            },
            {
                sats: 546n,
                outputScript:
                    '76a914cf76d8e334b149cb49ad1f95de339c3e6e9ed54188ac',
                token: {
                    tokenId:
                        '0387947fd575db4fb19a3e322f635dec37fd192b5941625b66bc4b2c3008cbf0',
                    tokenType: {
                        protocol: 'ALP',
                        type: 'ALP_TOKEN_TYPE_STANDARD',
                        number: 0,
                    },
                    atoms: 0n,
                    isMintBaton: true,
                    entryIdx: 0,
                },
            },
            {
                sats: 546n,
                outputScript:
                    '76a91438d2e1501a485814e2849552093bb0588ed9acbb88ac',
                token: {
                    tokenId:
                        '0387947fd575db4fb19a3e322f635dec37fd192b5941625b66bc4b2c3008cbf0',
                    tokenType: {
                        protocol: 'ALP',
                        type: 'ALP_TOKEN_TYPE_STANDARD',
                        number: 0,
                    },
                    atoms: 0n,
                    isMintBaton: true,
                    entryIdx: 0,
                },
                spentBy: {
                    txid: 'd6fafa6977a24133789fcdec7922c14cfaf3120072ae82e21abcc3a68b6ed6c7',
                    outIdx: 0,
                },
            },
            {
                sats: 546n,
                outputScript:
                    '76a914a66cd958eed093c209643e62a7f56fc9eb46622c88ac',
                token: {
                    tokenId:
                        '0387947fd575db4fb19a3e322f635dec37fd192b5941625b66bc4b2c3008cbf0',
                    tokenType: {
                        protocol: 'ALP',
                        type: 'ALP_TOKEN_TYPE_STANDARD',
                        number: 0,
                    },
                    atoms: 0n,
                    isMintBaton: true,
                    entryIdx: 0,
                },
            },
            {
                sats: 1602n,
                outputScript:
                    '76a914cf76d8e334b149cb49ad1f95de339c3e6e9ed54188ac',
                spentBy: {
                    txid: '83b7d403d5b7ae295ff1e2a66ef220ed6ae190b019df4822643bea545518734a',
                    outIdx: 1,
                },
            },
        ],
        lockTime: 0,
        timeFirstSeen: 1740005671,
        size: 414,
        isCoinbase: false,
        tokenEntries: [
            {
                tokenId:
                    '0387947fd575db4fb19a3e322f635dec37fd192b5941625b66bc4b2c3008cbf0',
                tokenType: {
                    protocol: 'ALP',
                    type: 'ALP_TOKEN_TYPE_STANDARD',
                    number: 0,
                },
                txType: 'GENESIS',
                isInvalid: false,
                burnSummary: '',
                failedColorings: [],
                actualBurnAtoms: 0n,
                intentionalBurnAtoms: 0n,
                burnsMintBatons: false,
            },
        ],
        tokenFailedParsings: [],
        tokenStatus: 'TOKEN_STATUS_NORMAL',
        isFinal: true,
        block: {
            height: 884824,
            hash: '0000000000000000042420f0d007398b85c2a4b02d894575de72441c055099fc',
            timestamp: 1740006660,
        },
    },
};

/** FIRMA genesis minter pubkey — order book only lists this maker or the active wallet. */
export const FIRMA_MINTER_PK_HEX =
    '03fba49912622cf8bb5b3729b1b5da3e72c6b57d369c8647f6cc7c6cbed510d105';

/**
 * XECX genesis / minter pubkey (ecash:qzd5s72xhgjvr4sjfzafjt3a2vcstn4pfvs4c84egx).
 * Order book only lists this maker or the active wallet.
 */
export const XECX_MINTER_PK_HEX =
    '03e4d137b0fd6d8cfbb6aeb1d83c6cb33b19143e7faeacc1d79cf6f052dc56f650';

/**
 * Tokens that may resolve Patron usernames on send.
 * XEC and every other token stay address / contact only.
 */
export const FIRMA_USERNAME_TOKEN_IDS: ReadonlySet<string> = new Set([
    FIRMA.tokenId,
    FIRMA_CHF_TOKEN_ID,
    FIRMA_EUR_TOKEN_ID,
]);

/**
 * True when this tokenId is FIRMA, fCHF, or fEUR.
 *
 * @param tokenId Selected or BIP21 token_id
 */
export const isFirmaUsernameTokenId = (
    tokenId: string | null | undefined,
): boolean =>
    typeof tokenId === 'string' && FIRMA_USERNAME_TOKEN_IDS.has(tokenId);
